import React, { Fragment } from 'react';
import { toast } from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import axios from '../../utils/axiosConfig';
import { changeUsersData, getUsersData, disableMFA } from '../../redux/actions/userActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import Table, { TableButton, TableButtons } from '../../components/Table';
import Pagination from '../../components/Pagination';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';
import AddUserModal from './AddUserModal';
import ResetPasswordModal from './ResetPasswordModal';
import AlertPrefsModal from './AlertPrefsModal';
import DeleteUserModal from './DeleteUserModal';
import PromptHistoryModal from './PromptHistoryModal';
import TempPasswordModal from './TempPasswordModal';
import UsersStyles from './Users.module.css';

const FRONTEND_USERS_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	USER_ACTION_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	MFA_RESET_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	PROMPT_HISTORY_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildUsersSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const UsersScreen = () => {
	const dispatch = useDispatch();
	const [ searchKey, setSearchKey ] = React.useState('');

	const [ dataLoading, setDataLoading ] = React.useState(false);
	const [ usersData, setUsersData ] = React.useState({});
	const [ usersTotalRows, setUsersTotalRows ] = React.useState(0);
	const [ usersRows, setUsersRows ] = React.useState([]);
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);
	const [ promptHistoryModalStatus, setPromptHistoryModalStatus ] = React.useState({ username: '', company: '', status: false });
	const [ promptHistoryRows, setPromptHistoryRows ] = React.useState([]);
	const [ promptHistoryLoading, setPromptHistoryLoading ] = React.useState(false);
	const [ promptHistoryError, setPromptHistoryError ] = React.useState('');
	const [ promptHistorySearchUser, setPromptHistorySearchUser ] = React.useState('');
	
	// Company-related state
	const [ currentAdminCompany, setCurrentAdminCompany ] = React.useState('');
	const [ availableCompanies, setAvailableCompanies ] = React.useState([]);
	const [ isProvider, setIsProvider ] = React.useState(false);
	// Air-gap deployment: create username-only accounts (no email/invite required).
	const [ airgap, setAirgap ] = React.useState(false);

	const [ pageNumber, setPageNumber ] = React.useState(1);
	const pageLimit = 10;

	const setUsersSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildUsersSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearUsersSupportError = React.useCallback((force = false) => {
		if (!force) return;
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [ supportErrorMessage ]);

	const notifyUsersSupportError = React.useCallback(
		(errorCode, pageInfo, sourceMessage) => {
			const formattedError = buildUsersSupportError(errorCode, pageInfo, sourceMessage);
			toast.error(formattedError);
			setUsersSupportError(errorCode, pageInfo, sourceMessage);
		},
		[ setUsersSupportError ]
	);

	const closePromptHistoryModal = React.useCallback(() => {
		setPromptHistoryModalStatus({ username: '', company: '', status: false });
		setPromptHistoryRows([]);
		setPromptHistoryError('');
		setPromptHistoryLoading(false);
		setPromptHistorySearchUser('');
	}, []);

	const loadPromptHistory = React.useCallback(
		async ({ username = '', usernameSearch = '', company = '', searchMode = false } = {}) => {
			setPromptHistoryModalStatus({ username: username || usernameSearch, company, status: true, searchMode });
			setPromptHistoryRows([]);
			setPromptHistoryError('');
			setPromptHistoryLoading(true);
			try {
				const params = { limit: 100 };
				if (username) {
					params.username = username;
				} else if (usernameSearch) {
					params.username_search = usernameSearch;
				}
				if (company) {
					params.company = company;
				}
				const response = await axios.get('/api/ai-chat/prompts', {
					params,
				});
				if (response.data && response.data.status) {
					setPromptHistoryRows(Array.isArray(response.data.prompts) ? response.data.prompts : []);
				} else {
					const message = response.data?.msg || 'Prompt history request did not return success';
					setPromptHistoryError(message);
					notifyUsersSupportError(
						FRONTEND_USERS_ERROR_CODES.PROMPT_HISTORY_LOAD_FAILED,
						'Users Page / AI Prompt History Load Failed',
						message
					);
				}
			} catch (error) {
				const message = error?.response?.data?.msg || error?.message || String(error);
				setPromptHistoryError(message);
				notifyUsersSupportError(
					FRONTEND_USERS_ERROR_CODES.PROMPT_HISTORY_LOAD_FAILED,
					'Users Page / AI Prompt History Request Error',
					message
				);
			} finally {
				setPromptHistoryLoading(false);
			}
		},
		[ notifyUsersSupportError ]
	);

	const handleViewPromptHistory = React.useCallback(
		(username, company) => {
			setPromptHistorySearchUser(username || '');
			loadPromptHistory({ username, company });
		},
		[ loadPromptHistory ]
	);

	const handleOpenPromptHistorySearch = React.useCallback(
		() => {
			setPromptHistorySearchUser('');
			loadPromptHistory({ searchMode: true });
		},
		[ loadPromptHistory ]
	);

	const handleSearchPromptHistory = React.useCallback(
		(event) => {
			event?.preventDefault?.();
			loadPromptHistory({ usernameSearch: promptHistorySearchUser.trim(), searchMode: true });
		},
		[ loadPromptHistory, promptHistorySearchUser ]
	);

	const getData = React.useCallback(
		async () => {
			setDataLoading(true);
			try {
				const data = await dispatch(getUsersData());
				if (data && data.status) {
					setUsersData(data);
					// Set admin company and available companies from response
					if (data.userCompany) {
						setCurrentAdminCompany(data.userCompany);
					}
					if (data.availableCompanies) {
						setAvailableCompanies(data.availableCompanies);
					}
					setIsProvider(Boolean(data.isProvider));
					setAirgap(Boolean(data.airgap));
					clearUsersSupportError(true);
				} else {
					setUsersData({});
					setCurrentAdminCompany('');
					setAvailableCompanies([]);
					setUsersSupportError(
						FRONTEND_USERS_ERROR_CODES.INITIAL_LOAD_FAILED,
						'Users Page / Initial Data Load Failed',
						data?.msg || (typeof data === 'string' ? data : 'Users request did not return success')
					);
				}
			} catch (error) {
				setUsersData({});
				setCurrentAdminCompany('');
				setAvailableCompanies([]);
				setUsersSupportError(
					FRONTEND_USERS_ERROR_CODES.INITIAL_LOAD_FAILED,
					'Users Page / Initial Data Request Error',
					error?.message || String(error)
				);
			} finally {
				setDataLoading(false);
			}
		},
		[ clearUsersSupportError, dispatch, setUsersSupportError ]
	);

	React.useEffect(
		() => {
			getData();
		},
		[ getData ]
	);

	React.useEffect(
		() => {
			if (
				usersData &&
				Object.keys(usersData).length > 0 &&
				Array.isArray(usersData.userRows) &&
				usersData.userRows.length > 0
			) {
				if (searchKey) {
					const usersRequestRows = usersData.userRows.filter((row) =>
						row.find((el) => String(el ?? '').toLowerCase().includes(String(searchKey ?? '').toLowerCase()))
					);
					setUsersTotalRows(usersRequestRows.length);
					setUsersRows(usersRequestRows.slice(0, pageLimit));
				} else {
					setUsersTotalRows(usersData.userRows.length);
					setUsersRows(usersData.userRows.slice(0, pageLimit));
				}
			} else {
				setUsersTotalRows(0);
				setUsersRows([]);
			}
		},
		[ usersData, searchKey ]
	);

	let nextContentFrom = 0;
	const handlePageChange = async (currentPage) => {
		setPageNumber(currentPage.selected + 1);

		nextContentFrom = currentPage.selected * pageLimit;
		const nextContentTo = currentPage.selected * pageLimit + pageLimit;
		setUsersRows(
			usersData &&
				Object.keys(usersData).length > 0 &&
				usersData.userRows.length > 0 &&
				usersData.userRows
					.filter((row) => row.find((el) => String(el ?? '').toLowerCase().includes(String(searchKey ?? '').toLowerCase())))
					.slice(nextContentFrom, nextContentTo)
		);
	};

	// ================= Add/Update user start ====================
	const [ addUserModalStatus, setAddUserModalStatus ] = React.useState({ data: [], status: false });
	// Set to { username, tempPassword } after creating a user while email is off,
	// so the one-time password is shown in a copyable dialog.
	const [ tempPwInfo, setTempPwInfo ] = React.useState(null);
	const [ userName, setUserName ] = React.useState({ value: '', error: '' });
	const [ userRole, setUserRole ] = React.useState({ value: '', error: '' });
	const [ userCompany, setUserCompany ] = React.useState({ value: '', error: '' });
	const [ userEmail, setUserEmail ] = React.useState({ value: '', error: '' });

	React.useEffect(
		() => {
			if (addUserModalStatus.data && addUserModalStatus.data.length === 3) {
				// Editing user: [username, role, company]
				setUserName({ value: addUserModalStatus.data[0] });
				setUserRole({ value: addUserModalStatus.data[1] });
				setUserCompany({ value: addUserModalStatus.data[2] });
			}
		},
		[ addUserModalStatus ]
	);

	const closeModal = () => {
		setAddUserModalStatus({ data: [], status: false });
		setUserName({ value: '', error: '' });
		setUserRole({ value: '', error: '' });
		setUserCompany({ value: '', error: '' });
		setUserEmail({ value: '', error: '' });
	};

	const validatePassword = (password) => {
		// Matches the backend policy (app/core/password_policy.py): lowercase,
		// uppercase, digit, and one special from a broad set (includes '-', which
		// Apple's generated passwords use). Length (>=12) is checked separately.
		return /[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password)
			&& /[!@#$%^&*()\-_=+[\]{};:,.<>?\/|\\~`"']/.test(password);
	};

	const handleAddUser = async () => {
		const isEdit = addUserModalStatus.data && addUserModalStatus.data.length === 3;
		// On create, the email address IS the username, so there is no separate
		// username field to validate — the email check below covers it.
		if (isEdit && (userName.value === '' || userName.value.trim() === '')) {
			setUserName({ ...userName, error: 'Please enter username' });
		} else if (userRole.value === '' || userRole.value.trim() === '') {
			setUserRole({ ...userRole, error: 'Please select user role' });
		} else if (isProvider && (userCompany.value === '' || userCompany.value.trim() === '')) {
			// FLC admins must select a company
			setUserCompany({ ...userCompany, error: 'Please select a company' });
		} else {
			const formData = new FormData();
			if (isEdit) {
				// Update existing user
				formData.append('btn', 'UpdateAccount');
				formData.append('Username', userName.value);
				formData.append('UserRole', userRole.value);
				formData.append('UserCompany', userCompany.value);

				try {
					const data = await dispatch(changeUsersData(formData));

					if (data && data.status === true) {
						toast.success(data.msg);
						clearUsersSupportError(true);
						getData();
						closeModal();
					} else {
						notifyUsersSupportError(
							FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
							'Users Page / Update User Failed',
							data?.msg || (typeof data === 'string' ? data : 'Update user request did not return success')
						);
					}
				} catch (error) {
					notifyUsersSupportError(
						FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
						'Users Page / Update User Request Error',
						error?.message || String(error)
					);
				}
			} else if (airgap) {
				// Air-gapped: create a username-only account (no email/invite — the
				// box has no reachable mailbox and the user logs in from a LAN
				// workstation). The single field holds a plain username; email is not
				// collected. The backend returns a one-time temp password to hand over.
				const uname = userEmail.value.trim();
				if (uname === '') {
					setUserEmail({ ...userEmail, error: 'Please enter a username' });
				} else if (/\s/.test(uname)) {
					setUserEmail({ ...userEmail, error: 'Username cannot contain spaces' });
				} else {
					formData.append('btn', 'CreateAccount');
					formData.append('NewUser', uname);
					formData.append('NewRole', userRole.value);
					formData.append('NewEmail', '');
					if (isProvider) {
						formData.append('NewCompany', userCompany.value);
					} else {
						formData.append('NewCompany', currentAdminCompany);
					}
					try {
						const data = await dispatch(changeUsersData(formData));
						if (data && data.status === true) {
							clearUsersSupportError(true);
							getData();
							closeModal();
							if (data.tempPassword) {
								setTempPwInfo({ username: data.username || uname, tempPassword: data.tempPassword });
							} else {
								toast.success(data.msg);
							}
						} else {
							notifyUsersSupportError(
								FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
								'Users Page / Create User Failed',
								data?.msg || (typeof data === 'string' ? data : 'Create user request did not return success')
							);
						}
					} catch (error) {
						notifyUsersSupportError(
							FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
							'Users Page / Create User Request Error',
							error?.message || String(error)
						);
					}
				}
			} else {
				// Create new user — invite flow: the user gets an email to set their
				// own password, so we collect an email address instead of a password.
				const email = userEmail.value.trim();
				if (email === '') {
					setUserEmail({ ...userEmail, error: 'Please enter an email address' });
				} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
					setUserEmail({ ...userEmail, error: 'Please enter a valid email address' });
				} else {
					// The email address is the username for new accounts.
					formData.append('btn', 'CreateAccount');
					formData.append('NewUser', email);
					formData.append('NewRole', userRole.value);
					formData.append('NewEmail', email);
					// For FLC admins, use selected company; for others, backend will use admin's company
					if (isProvider) {
						formData.append('NewCompany', userCompany.value);
					} else {
						formData.append('NewCompany', currentAdminCompany);
					}

					try {
						const data = await dispatch(changeUsersData(formData));

						if (data && data.status === true) {
							clearUsersSupportError(true);
							getData();
							closeModal();
							// Email off: the backend returns a one-time temp password to hand
							// over. Show it in a copyable dialog (not a toast) — it can't be
							// retrieved again.
							if (data.tempPassword) {
								setTempPwInfo({ username: data.username || email, tempPassword: data.tempPassword });
							} else {
								toast.success(data.msg);
							}
						} else {
							notifyUsersSupportError(
								FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
								'Users Page / Create User Failed',
								data?.msg || (typeof data === 'string' ? data : 'Create user request did not return success')
							);
						}
					} catch (error) {
						notifyUsersSupportError(
							FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
							'Users Page / Create User Request Error',
							error?.message || String(error)
						);
					}
				}
			}
		}
	};
	// ================= Add/Update user End ====================

	// ================= Resend invite (pending accounts) ====================
	const handleResendInvite = async (username) => {
		const formData = new FormData();
		formData.append('btn', 'ResendInvite');
		formData.append('Username', username);
		try {
			const data = await dispatch(changeUsersData(formData));
			if (data && data.status === true) {
				toast.success(data.msg);
				clearUsersSupportError(true);
				getData();
			} else {
				notifyUsersSupportError(
					FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
					'Users Page / Resend Invite Failed',
					data?.msg || (typeof data === 'string' ? data : 'Resend invite request did not return success')
				);
			}
		} catch (error) {
			notifyUsersSupportError(
				FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
				'Users Page / Resend Invite Request Error',
				error?.message || String(error)
			);
		}
	};

	// ================= Alert email preferences ====================
	const [ alertPrefsModalStatus, setAlertPrefsModalStatus ] = React.useState({ status: false, username: '' });
	const [ alertPrefsCategories, setAlertPrefsCategories ] = React.useState([]);
	const [ alertPrefsSelected, setAlertPrefsSelected ] = React.useState([]);
	const [ alertPrefsLoading, setAlertPrefsLoading ] = React.useState(false);

	const openAlertPrefs = async (username) => {
		setAlertPrefsModalStatus({ status: true, username });
		setAlertPrefsLoading(true);
		setAlertPrefsCategories([]);
		setAlertPrefsSelected([]);
		try {
			const { data } = await axios.get('/api/user-alert-prefs', { params: { username } });
			if (data && data.status) {
				setAlertPrefsCategories(data.categories || []);
				setAlertPrefsSelected(data.selected || []);
			} else {
				notifyUsersSupportError(
					FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
					'Users Page / Load Alert Prefs Failed',
					data?.msg || 'Could not load alert preferences'
				);
			}
		} catch (error) {
			notifyUsersSupportError(
				FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
				'Users Page / Load Alert Prefs Error',
				error?.message || String(error)
			);
		} finally {
			setAlertPrefsLoading(false);
		}
	};

	const toggleAlertPref = (key) => {
		setAlertPrefsSelected((prev) =>
			prev.includes(key) ? prev.filter((k) => k !== key) : [ ...prev, key ]);
	};

	const closeAlertPrefsModal = () => {
		setAlertPrefsModalStatus({ status: false, username: '' });
		setAlertPrefsCategories([]);
		setAlertPrefsSelected([]);
	};

	const handleSaveAlertPrefs = async () => {
		try {
			const { data } = await axios.post('/api/user-alert-prefs', {
				username: alertPrefsModalStatus.username,
				categories: alertPrefsSelected,
			});
			if (data && data.status === true) {
				toast.success(data.msg);
				clearUsersSupportError(true);
				closeAlertPrefsModal();
			} else {
				notifyUsersSupportError(
					FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
					'Users Page / Save Alert Prefs Failed',
					data?.msg || (typeof data === 'string' ? data : 'Save alert preferences did not return success')
				);
			}
		} catch (error) {
			notifyUsersSupportError(
				FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
				'Users Page / Save Alert Prefs Error',
				error?.message || String(error)
			);
		}
	};

	// ================= Reset User Password Start ====================
	const [ resetPasswordModalStatus, setResetPasswordModalStatus ] = React.useState({ data: [], status: false });
	const [ newPassword, setNewPassword ] = React.useState({ value: '', error: '' });
	const [ confirmPassword, setConfirmPassword ] = React.useState({ value: '', error: '' });

	const closeResetPasswordModal = () => {
		setResetPasswordModalStatus({ data: [], status: false });
		setNewPassword({ value: '', error: '' });
		setConfirmPassword({ value: '', error: '' });
	};

	const handleResetPassword = async () => {
		if (newPassword.value === '' || newPassword.value.trim() === '') {
			setNewPassword({ ...newPassword, error: 'Please enter new password' });
		} else if (!validatePassword(newPassword.value) || newPassword.value.length < 12) {
			setNewPassword({ ...newPassword, error: 'Please enter strong password' });
		} else if (confirmPassword.value === '' || confirmPassword.value.trim() === '') {
			setConfirmPassword({ ...confirmPassword, error: 'Please confirm new password' });
		} else if (newPassword.value !== confirmPassword.value) {
			setConfirmPassword({ ...confirmPassword, error: 'Passwords do not match' });
		} else if (resetPasswordModalStatus.data && resetPasswordModalStatus.data.length === 2) {
			const formData = new FormData();
			formData.append('btn', 'ResetPassword');
			formData.append('Username', resetPasswordModalStatus.data[0]);
			formData.append('Password', newPassword.value);

			try {
				const data = await dispatch(changeUsersData(formData));

				if (data && data.status === true) {
					toast.success(data.msg);
					clearUsersSupportError(true);
					getData();
					closeResetPasswordModal();
				} else {
					notifyUsersSupportError(
						FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
						'Users Page / Reset Password Failed',
						data?.msg || (typeof data === 'string' ? data : 'Reset password request did not return success')
					);
				}
			} catch (error) {
				notifyUsersSupportError(
					FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
					'Users Page / Reset Password Request Error',
					error?.message || String(error)
				);
			}
		} else {
			notifyUsersSupportError(
				FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
				'Users Page / Reset Password Validation Error',
				'Reset password modal is missing user context'
			);
		}
	};
	// ================= Reset User Password Start ====================

	// ================= Delete User Start ====================
	const [ deleteUserModalStatus, setDeleteUserModalStatus ] = React.useState({ data: [], status: false });

	const closeDeleteModal = () => {
		setDeleteUserModalStatus({ data: [], status: false });
	};

	const handleDeleteUser = async () => {
		if (deleteUserModalStatus.data && deleteUserModalStatus.data.length === 2) {
			const formData = new FormData();
			formData.append('btn', 'DeleteAccount');
			formData.append('Username', deleteUserModalStatus.data[0]);

			try {
				const data = await dispatch(changeUsersData(formData));

				if (data && data.status === true) {
					toast.success(data.msg);
					clearUsersSupportError(true);
					getData();
					closeDeleteModal();
				} else {
					notifyUsersSupportError(
						FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
						'Users Page / Delete User Failed',
						data?.msg || (typeof data === 'string' ? data : 'Delete user request did not return success')
					);
				}
			} catch (error) {
				notifyUsersSupportError(
					FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
					'Users Page / Delete User Request Error',
					error?.message || String(error)
				);
			}
		} else {
			notifyUsersSupportError(
				FRONTEND_USERS_ERROR_CODES.USER_ACTION_FAILED,
				'Users Page / Delete User Validation Error',
				'Delete user modal is missing user context'
			);
		}
	};
	// ================= Delete User End ====================

	// ================= Reset MFA Start ====================
	const handleResetMFA = async (username) => {
		if (window.confirm(`Are you sure you want to reset MFA for ${username}? They will need to set it up again on next login.`)) {
			try {
				const data = await dispatch(disableMFA(username));
				
				if (data && data.status) {
					toast.success(data.msg);
					clearUsersSupportError(true);
					getData();
				} else {
					notifyUsersSupportError(
						FRONTEND_USERS_ERROR_CODES.MFA_RESET_FAILED,
						'Users Page / Reset MFA Failed',
						data?.msg || (typeof data === 'string' ? data : 'Reset MFA request did not return success')
					);
				}
			} catch (error) {
				notifyUsersSupportError(
					FRONTEND_USERS_ERROR_CODES.MFA_RESET_FAILED,
					'Users Page / Reset MFA Request Error',
					error?.message || String(error)
				);
			}
		}
	};
	// ================= Reset MFA End ====================

	return (
		<Content
			currentMenu='admin'
			headerTitle='All Users'
			addBtn={true}
			addOnClick={() => setAddUserModalStatus({ data: [], status: true })}
			searchBox={true}
			searchPlaceholder='Search Users'
			searchValue={searchKey}
			searchOnChange={(e) => setSearchKey(e.target.value)}>
			{dataLoading ? (
				<Loader />
			) : usersData && Object.keys(usersData).length === 0 ? (
				<div>
					<NetworkError />
					{supportErrorMessage && (
						<div className={UsersStyles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={UsersStyles.supportErrorCode}>{supportErrorMessage}</code>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={UsersStyles.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={UsersStyles.copySupportErrorButton}
									onClick={() => clearUsersSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
				</div>
			) : (
				<Fragment>
					{supportErrorMessage && (
						<div className={UsersStyles.supportErrorBanner}>
							<div className={UsersStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={UsersStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={UsersStyles.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={UsersStyles.copySupportErrorButton}
									onClick={() => clearUsersSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
					{isProvider && (
						<div className={UsersStyles.promptHistoryToolbar}>
							<button
								type='button'
								className={UsersStyles.promptHistorySearchButton}
								onClick={handleOpenPromptHistorySearch}
							>
								Search AI Prompts
							</button>
						</div>
					)}
					<Table
						containerStyle={{ height: '75vh' }}
						tableHead={
							<tr>
								<th style={{ width: '48px' }}>#</th>
								{usersData.userCols &&
									usersData.userCols.length > 0 &&
									usersData.userCols.map((col, index) => <th key={col + index}>{col}</th>)}
								<th style={{ textAlign: 'right' }}>Actions</th>
							</tr>
						}
						tableRow={
							usersTotalRows > 0 && usersRows.length > 0 ? (
								usersRows.map((rows, index) => (
									<tr key={index}>
										<td>{index + 1}</td>
										<td>
											{rows[0]}
											{rows[6] === 1 && (
												<span style={{
													marginLeft: '8px',
													padding: '2px 8px',
													borderRadius: '4px',
													fontSize: '11px',
													fontWeight: '600',
													backgroundColor: 'var(--warning-color)',
													color: 'white'
												}}>
													Invite pending
												</span>
											)}
										</td>
										<td>{rows[1]}</td>
										<td>{rows[2] || 'N/A'}</td>
										<td>
											<span style={{
												padding: '4px 8px',
												borderRadius: '4px',
												fontSize: '12px',
												fontWeight: '600',
												backgroundColor: rows[3] === 1 ? 'var(--success-color)' : 'var(--warning-color)',
												color: 'white'
											}}>
												{rows[3] === 1 ? 'Enabled' : 'Disabled'}
											</span>
										</td>
										<td>{rows[4] || 'Never'}</td>
										<td>
											<TableButtons>
												<TableButton
													label='Edit User'
													icon='edit'
													onClick={() => setAddUserModalStatus({ data: [rows[0], rows[1], rows[2]], status: true })}
												/>
												{rows[6] === 1 ? (
													<TableButton
														label='Resend Invite'
														icon='edit'
														onClick={() => handleResendInvite(rows[0])}
													/>
												) : (
													<TableButton
														label='Reset Password'
														icon='lock'
														onClick={() => setResetPasswordModalStatus({ data: [rows[0], rows[1]], status: true })}
													/>
												)}
												{rows[3] === 1 && (
													<TableButton
														label='Reset MFA'
														icon='settings'
														onClick={() => handleResetMFA(rows[0])}
													/>
												)}
												<TableButton
													label='Alert Emails'
													icon='warning'
													onClick={() => openAlertPrefs(rows[0])}
												/>
												<TableButton
													label='AI Prompts'
													icon='eye'
													onClick={() => handleViewPromptHistory(rows[0], rows[2])}
												/>
												<TableButton
													label='Delete User'
													icon='trash'
													onClick={() => setDeleteUserModalStatus({ data: [rows[0], rows[1]], status: true })}
												/>
											</TableButtons>
										</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan={(usersData.userCols?.length || 5) + 2} style={{ textAlign: 'center' }}>
										No data found
									</td>
								</tr>
							)
						}
					/>

					<div className='paginationContainer'>
						<Pagination
							onPageChange={handlePageChange}
							rowsPerPage={pageLimit}
							totalPageSize={usersTotalRows}
							pageNumber={pageNumber}
						/>
					</div>
				</Fragment>
			)}

			<AddUserModal
				data={{
					addUserModalStatus,
					userName,
					setUserName,
					userRole,
					setUserRole,
					userCompany,
					setUserCompany,
					userEmail,
					setUserEmail,
					currentAdminCompany,
					availableCompanies,
					isProvider,
					airgap,
					closeModal,
					handleAddUser
				}}
			/>
			<ResetPasswordModal
				data={{
					resetPasswordModalStatus,
					newPassword,
					setNewPassword,
					confirmPassword,
					setConfirmPassword,
					closeResetPasswordModal,
					handleResetPassword
				}}
			/>
			<AlertPrefsModal
				data={{
					alertPrefsModalStatus,
					alertPrefsCategories,
					alertPrefsSelected,
					alertPrefsLoading,
					toggleAlertPref,
					closeAlertPrefsModal,
					handleSaveAlertPrefs
				}}
			/>
			<DeleteUserModal data={{ deleteUserModalStatus, closeDeleteModal, handleDeleteUser }} />
			<TempPasswordModal info={tempPwInfo} onClose={() => setTempPwInfo(null)} />
			<PromptHistoryModal
				data={{
					promptHistoryModalStatus,
					promptHistoryRows,
					promptHistoryLoading,
					promptHistoryError,
					promptHistorySearchUser,
					setPromptHistorySearchUser,
					canSearchPromptHistory: isProvider,
					handleSearchPromptHistory,
					closePromptHistoryModal
				}}
			/>
		</Content>
	);
};

export default UsersScreen;
